import React from 'react';
import {useNativeNode} from '../useNativeNode';
import {panelOpened} from '@packages/systems/panels';
import {TypeImplementation} from '@packages/systems/wfdl';
import {emotionStyled, controls} from '@packages/utilities/styled';
import {ASSET_MANAGER_HOT_SELECT_STARTED} from '@packages/systems/designer/actions';
import {
  useDesignerSelector,
  useDispatch,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {
  View,
  AddPanelRive64Icon,
  Divider,
  VStack,
  HStack,
  Button,
  Text,
  RefreshIcon,
  FieldImageSetIcon,
  GuideIcon,
  Note,
  NoteBody,
} from '@packages/systems/spring';
import {
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {useRiveContext} from './RiveContext';
import {useExpressionEditorImplementationContext} from '@packages/systems/ExpressionEditor-utils';
import {FileRecordType} from '@packages/systems/core/records/FileRecord';

const ASSETS_PANEL = 'assets';

const PreviewCard = emotionStyled(View)({
  gridColumn: '1 / 2',
  height: '$16',
  width: '$16',
  minHeight: '$16',
  minWidth: '$16',
  background: '$backgroundPrimary',
  borderColor: '$actionSecondaryBorder',
  borderWidth: controls.borderWidth,
  borderStyle: 'solid',
  borderRadius: controls.borderRadius,
  overflow: 'hidden',
});

const LabelText = emotionStyled(Text)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const VStackView = emotionStyled(VStack)({marginTop: '$2'});

type RivePreviewSettingsProps = {
  play: () => void;
  pause: () => void;
  isPlaying: boolean;
  isDisabled: boolean;
  label: string;
  asset: FileRecordType;
};

const RivePreviewSettings = ({
  play,
  pause,
  label,
  isPlaying,
  isDisabled,
  asset,
}: RivePreviewSettingsProps) => {
  const labelAnimation = isPlaying ? 'Pause' : 'Preview';

  return (
    <VStackView>
      <HStack>
        <PreviewCard data-automation-id="rive-preview-mode">
          <AddPanelRive64Icon size={64} />
        </PreviewCard>
        <HStack
          spacing={1}
          align="center"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {asset?.origin?.length ? (
            <GuideIcon colorVariant="secondary" />
          ) : null}
          <LabelText title={label}>{label}</LabelText>
        </HStack>
      </HStack>
      <Divider role="separator" />
      <Button onClick={isPlaying ? pause : play} isDisabled={isDisabled}>
        {labelAnimation}&nbsp;Animation
      </Button>
    </VStackView>
  );
};

const hotSelectingSelector = (state: DesignerStores) =>
  state.AssetManagerUIStore.hotSelecting;

export const path = TypeImplementation.Custom({
  render: function RiveSettings({expr}) {
    const hasNativeNode = useNativeNode() != null;
    const hotSelecting = useDesignerSelector(hotSelectingSelector);
    const fluxDispatch = useDispatch();
    const {env} = useExpressionEditorImplementationContext();

    const {
      play,
      pause,
      rive: {isPlaying},
      activeStateMachine,
      activeArtboard,
      isInstanceReady,
    } = useRiveContext();

    const literalVal = ContextualExpressions.getLiteral(stripAnn(expr));
    if (literalVal === null) {
      return null;
    }
    const literal = literalVal.value;
    // @ts-expect-error - TS2322 - Type 'Json' is not assignable to type 'State'.
    const state: State = literal;

    const asset =
      state && state.id && env.asset.get({type: 'all', id: state.id});

    const isPathSet = state && (state.src || state.id);

    const label = isPathSet ? 'Replace' : 'Choose';
    const PrefixIcon = (
      <View marginRight="$2">
        {isPathSet ? <RefreshIcon /> : <FieldImageSetIcon />}
      </View>
    );

    return (
      <>
        <Button
          isFullWidth
          onClick={() => {
            // flux dispatch to startHotSelect
            fluxDispatch({
              type: ASSET_MANAGER_HOT_SELECT_STARTED,
              payload: {mode: 'riveSettings'},
            });
            fluxDispatch(panelOpened(ASSETS_PANEL));
          }}
          isDisabled={hotSelecting} // only disable if hotselecting
          prefix={PrefixIcon}
        >
          <HStack isFullWidth>{label}&nbsp;file</HStack>
        </Button>
        {!hasNativeNode && (
          <Note>
            <NoteBody>
              Show this element on the canvas to preview it or choose an
              artboard and state machine.
            </NoteBody>
          </Note>
        )}
        {isPathSet ? (
          <RivePreviewSettings
            label={state.origFileName}
            isDisabled={
              !isInstanceReady ||
              activeStateMachine == null ||
              activeArtboard == null
            }
            pause={pause}
            play={play}
            isPlaying={isPlaying}
            asset={asset}
          />
        ) : null}
      </>
    );
  },
  system: {
    labelVariant: 'hidden',
    defaultValue: null,
  },
});
